import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
} from '@mui/material';
import { Home, ArrowBack } from '@mui/icons-material';

/**
 * 404 Not Found Page Component
 * Displayed when user navigates to a non-existent route
 */
const NotFound: React.FC = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          {/* 404 Header */}
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '4rem', sm: '6rem' },
              fontWeight: 'bold',
              color: 'primary.main',
              mb: 2,
            }}
          >
            404
          </Typography>

          {/* Title */}
          <Typography variant="h4" component="h2" gutterBottom>
            Page Not Found
          </Typography>

          {/* Description */}
          <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
            The page you're looking for doesn't exist or has been moved.
            It might be a broken link or you may have typed the URL incorrectly.
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              component={Link}
              to="/dashboard"
              sx={{ minWidth: 160 }}
            >
              Go Home
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              sx={{ minWidth: 160 }}
            >
              Go Back
            </Button>
          </Box>

          {/* Additional Help */}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="textSecondary">
              Need help? Contact support at{' '}
              <Typography
                component="a"
                href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL || 'support@martz-family.com'}`}
                color="primary"
                sx={{ textDecoration: 'none' }}
              >
                {process.env.REACT_APP_SUPPORT_EMAIL || 'support@martz-family.com'}
              </Typography>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default NotFound;